import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { toast } from 'react-toastify';
import EventBus from "../common/EventBus";
import adminService from "../services/admin.service";
import emsFolderService from "../services/emsFolder.service";

const EstimateFilePathManager = ({ clientId }) => {
    const { user: currentUser } = useSelector((state) => state.auth);

    const [sourcePaths, setSourcePaths] = useState([]);
    const [targetPaths, setTargetPaths] = useState([]);
    const [configId, setConfigId] = useState(null);
    const [timestamps, setTimestamps] = useState({ created: null, updated: null });

    const [loading, setLoading] = useState(false);

    const MAX_PATHS = 4;

    useEffect(() => {
        if (!currentUser || !currentUser.roles.includes("ROLE_ADMIN")) {
            return;
        }
        if (clientId) {
            fetchEmsFolderConfig(clientId);
        } else {
            // Reset if clientId drops
            setSourcePaths([]);
            setTargetPaths([]);
            setConfigId(null);
            setTimestamps({ created: null, updated: null });
        }
    }, [currentUser, clientId]);

    const fetchEmsFolderConfig = (clientId) => {
        setLoading(true);
        setSourcePaths([]);
        setTargetPaths([]);
        setConfigId(null);
        setTimestamps({ created: null, updated: null });

        emsFolderService.getEmsFolder(clientId).then(
            (response) => {
                if (response.data && response.data.id) {
                    setConfigId(response.data.id);
                    setSourcePaths(response.data.source || []);
                    setTargetPaths(response.data.target || []);
                    setTimestamps({
                        created: response.data.createdAt,
                        updated: response.data.updatedAt
                    });
                } else {
                    // Normal state; no configuration exists.
                }
                setLoading(false);
            },
            (error) => {
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to load configuration." });
                setLoading(false);
            }
        );
    };

    const handlePathChange = (type, index, value) => {
        if (type === 'source') {
            const newPaths = [...sourcePaths];
            newPaths[index] = value;
            setSourcePaths(newPaths);
        } else {
            const newPaths = [...targetPaths];
            newPaths[index] = value;
            setTargetPaths(newPaths);
        }
    };

    const addPath = (type) => {
        if (type === 'source') {
            if (sourcePaths.length >= MAX_PATHS) return toast.warning(`Max ${MAX_PATHS} source paths allowed.`);
            setSourcePaths([...sourcePaths, ""]);
        } else {
            if (targetPaths.length >= MAX_PATHS) return toast.warning(`Max ${MAX_PATHS} target paths allowed.`);
            setTargetPaths([...targetPaths, ""]);
        }
    };

    const removePath = (type, index) => {
        if (type === 'source') {
            const newPaths = sourcePaths.filter((_, i) => i !== index);
            setSourcePaths(newPaths);
        } else {
            const newPaths = targetPaths.filter((_, i) => i !== index);
            setTargetPaths(newPaths);
        }
    };

    const isValidWindowsPath = (path) => {
        if (/[<>"|?*/]/.test(path)) return false;
        if (/^[a-zA-Z]:\\/.test(path) || /^\\\\/.test(path)) return true;
        return false;
    };

    const saveConfig = () => {
        if (!clientId) return;

        // Filter out empty strings before saving
        const cleanSource = sourcePaths.map(s => s.trim()).filter(s => s !== "");
        const cleanTarget = targetPaths.map(s => s.trim()).filter(s => s !== "");

        const invalidPaths = [...cleanSource, ...cleanTarget].filter(p => !isValidWindowsPath(p));
        if (invalidPaths.length > 0) {
            EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Paths must be valid absolute Windows paths (e.g. C:\\data or \\\\server\\share). Forward slashes (/) are not allowed." });
            return;
        }

        const allPaths = [...cleanSource, ...cleanTarget];
        const uniquePaths = new Set(allPaths.map(p => p.toLowerCase()));
        if (uniquePaths.size !== allPaths.length) {
            EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Duplicate paths are not allowed. Each path must be unique across both Source and Target lists." });
            return;
        }

        if (cleanSource.length > MAX_PATHS || cleanTarget.length > MAX_PATHS) {
            EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: `A maximum of ${MAX_PATHS} items are allowed per list.` });
            return;
        }

        const payload = {
            clientId: clientId,
            source: cleanSource,
            target: cleanTarget
        };

        setLoading(true);
        emsFolderService.saveEmsFolder(clientId, payload).then(
            (response) => {
                toast.success("Configuration saved successfully.");
                // Refresh data to get exact IDs and DB timestamps
                fetchEmsFolderConfig(clientId);
            },
            (error) => {
                const _content = (error.response && error.response.data) || error.message;
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to save: " + _content });
                setLoading(false);
            }
        );
    };

    const deleteConfig = () => {
        if (!clientId || !configId) return;

        if (window.confirm("Are you sure you want to completely delete this configuration?")) {
            setLoading(true);
            emsFolderService.deleteEmsFolder(clientId).then(
                (response) => {
                    toast.success("Configuration deleted.");
                    fetchEmsFolderConfig(clientId); // Will effectively reset the form
                },
                (error) => {
                    EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to delete configuration." });
                    setLoading(false);
                }
            );
        }
    };

    if (!currentUser) return <Navigate to="/login" />;
    if (!currentUser.roles.includes("ROLE_ADMIN")) {
        return (
            <div className="container">
                <header className="jumbotron">
                    <h3><strong>Unauthorized:</strong> You must be an administrator to access this page.</h3>
                </header>
            </div>
        );
    }

    return (
        <div className="mt-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5>Estimate File Paths Config</h5>
                {loading && <span className="spinner-border spinner-border-sm text-primary" role="status"></span>}
            </div>

            {clientId && (
                <div className="card shadow-sm border-secondary">
                    <div className="card-header bg-light d-flex justify-content-between align-items-center">
                        <span className="text-muted small">
                            {configId ? 'File paths are loaded for this client.' : 'No paths configured yet.'}
                        </span>
                        {configId && (
                            <span className="badge bg-secondary">
                                Last Updated: {timestamps.updated ? new Date(timestamps.updated).toLocaleString() : 'N/A'}
                            </span>
                        )}
                    </div>
                    <div className="card-body">
                        <div className="row">
                            {/* Source Paths Column */}
                            <div className="col-md-6 border-end">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h5>Source Paths</h5>
                                    <button
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => addPath('source')}
                                        disabled={sourcePaths.length >= MAX_PATHS || loading}
                                    >
                                        + Add Path
                                    </button>
                                </div>

                                {sourcePaths.length === 0 && <p className="text-muted fst-italic">No source paths defined.</p>}

                                {sourcePaths.map((path, index) => (
                                    <div key={`source-${index}`} className="input-group mb-2">
                                        <span className="input-group-text">{index + 1}</span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={path}
                                            onChange={(e) => handlePathChange('source', index, e.target.value)}
                                            placeholder="C:\\example\\path"
                                            disabled={loading}
                                        />
                                        <button
                                            className="btn btn-outline-danger"
                                            onClick={() => removePath('source', index)}
                                            disabled={loading}
                                        >
                                            X
                                        </button>
                                    </div>
                                ))}
                                {sourcePaths.length >= MAX_PATHS && <small className="text-danger d-block mt-2">Maximum limit of {MAX_PATHS} reached.</small>}
                            </div>

                            {/* Target Paths Column */}
                            <div className="col-md-6">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h5>Target Paths</h5>
                                    <button
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => addPath('target')}
                                        disabled={targetPaths.length >= MAX_PATHS || loading}
                                    >
                                        + Add Path
                                    </button>
                                </div>

                                {targetPaths.length === 0 && <p className="text-muted fst-italic">No target paths defined.</p>}

                                {targetPaths.map((path, index) => (
                                    <div key={`target-${index}`} className="input-group mb-2">
                                        <span className="input-group-text">{index + 1}</span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={path}
                                            onChange={(e) => handlePathChange('target', index, e.target.value)}
                                            placeholder="C:\\example\\path"
                                            disabled={loading}
                                        />
                                        <button
                                            className="btn btn-outline-danger"
                                            onClick={() => removePath('target', index)}
                                            disabled={loading}
                                        >
                                            X
                                        </button>
                                    </div>
                                ))}
                                {targetPaths.length >= MAX_PATHS && <small className="text-danger d-block mt-2">Maximum limit of {MAX_PATHS} reached.</small>}
                            </div>
                        </div>

                        <hr className="mt-4 mb-4" />

                        <div className="d-flex justify-content-end gap-2">
                            {configId && (
                                <button
                                    className="btn btn-danger"
                                    onClick={deleteConfig}
                                    disabled={loading}
                                >
                                    Delete Configuration
                                </button>
                            )}
                            <button
                                className="btn btn-success px-4"
                                onClick={saveConfig}
                                disabled={loading}
                            >
                                Save Configuration
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EstimateFilePathManager;
